<script>
    import {t} from "$utils/lang.js";
    import {Select, TextInput} from "m7-library";

    /**
     * @property {import('$lib/userStore.svelte').Address} address
     */
    import ValidationError from "$component/ValidationError.svelte";

    import {store as userStore} from '$lib/userStore.svelte';
    import {query as gqlQuery} from '$lib/graphqlClient.js';
    import {untrack} from "svelte";

    let {address, errors = {}} = $props();

    let addressInput = $derived(
        [address.street, address.streetNo, address.zip, address.city]
            .filter(Boolean)
            .join(", "),
    );
    let suggestions = $state([]);
    let isOpen = $state(false);
    let isLoading = $state(false);
    let sessionToken = $state("");
    let debounceTimer;

    let showFullAddress = $derived(
        !!(address.street || address.streetNr || address.city || address.zip),
    );

    let countries = $state([]);
    $effect(() => {
        // Auch den Namen vergleichen: er kommt nachgeladen (userStore countryName). reference: [lib/userStore.svelte 1]
        if (countries.length > 1 || countries.length === 1 && countries[0].value === address.countryId && countries[0].label === address.country) return;

        countries = [{value: address.countryId, label: address.country}]
    });

    // Sync countryCode + country name when the user picks a different country from the list.
    // fetchSuggestions passes address.countryCode to Google Places — must stay current.
    $effect(() => {
        if (countries.length <= 1) return;
        const selected = countries.find(c => c.value === address.countryId);
        if (selected?.code) {
            untrack(() => {
                address.countryCode = selected.code;
                address.country = selected.label;
            });
        }
    });

    // Generate Session Token on mount (reduces Google API costs)
    $effect(() => {
        if (!sessionToken && typeof crypto !== "undefined") {
            sessionToken = crypto.randomUUID();
        }
    });

    /**
     * Handles typing in the input field with Debounce
     */
    function handleInput(event) {
        // Support native event or custom component event
        const val = event.target ? event.target.value : event;
        addressInput = val;

        if (debounceTimer) clearTimeout(debounceTimer);

        if (!val || val.length < 3) {
            suggestions = [];
            isOpen = false;
            return;
        }

        debounceTimer = setTimeout(() => {
            fetchSuggestions(val);
        }, 400); // 400ms delay
    }

    async function fetchCountries() {
        if (countries.length > 1) return;
        isLoading = true;
        try {
            // oxid-proxy GraphQL (replaces the legacy simple-api.php endpoint).
            // The `countries` query carries the iso code that the autocomplete
            // needs for the google-places country filter.
            const data = await gqlQuery(`query Countries { countries { id title code } }`);
            countries = (data?.countries ?? []).map((country) => ({
                value: country.id,
                label: country.title,
                code: country.code,
            }));
        } catch (e) {
            console.error("Address Service Error:", e);
        } finally {
            isLoading = false;
        }
    }

    /**
     * Fetches autocomplete suggestions
     */
    async function fetchSuggestions(term) {
        isLoading = true;
        // Resolve country code from loaded list — more reliable than address.countryCode
        // which may not yet be synced if the $effect hasn't flushed.
        const countryCode = countries.find(c => c.value === address.countryId)?.code || address.countryCode;
        try {
            const params = new URLSearchParams({
                term: term,
                session_token: sessionToken || "",
                country: countryCode,
            });

            // oxid-proxy cached Google Places endpoint — Redis-cached so
            // wiederholte Test-Runs / Production-Hot-Queries die Google-API
            // nicht erneut belasten.
            // credentials: 'include' — sends the OXID sid cookie (the endpoint
            // gates on it via requireSession) AND resends staging's HTTP
            // basic-auth credentials. Without it, fetch() omits the basic-auth
            // header → Apache re-challenges → 401 + browser auth prompt. The
            // GraphQL client sets the same flag for this reason.
            const res = await fetch(`/oxid-proxy/api/google-places/autocomplete?${params.toString()}`, {credentials: 'include'});
            const data = await res.json();

            if (Array.isArray(data)) {
                suggestions = data;
                isOpen = data.length > 0;
            } else {
                suggestions = [];
                isOpen = false;
            }
        } catch (e) {
            console.error("Google Places Error:", e);
            suggestions = [];
        } finally {
            isLoading = false;
        }
    }

    /**
     * Handles selection of a suggestion
     */
    async function handleSelect(item) {
        // 1. Update visual input immediately
        addressInput = item.description;
        isOpen = false;

        // 2. Fetch Details
        try {
            const params = new URLSearchParams({
                place_id: item.place_id,
                session_token: sessionToken || "",
            });

            const res = await fetch(`/oxid-proxy/api/google-places/details?${params.toString()}`, {credentials: 'include'});
            const details = await res.json();

            if (details && !details.error) {
                // 3. Update user data — prefer the structured components.
                address.street = details.route;
                address.streetNo = details.street_number;
                address.zip = details.zip;
                address.city = details.city;

                // US fallback: for street-level place_ids Google sometimes omits
                // the `street_number` component, leaving the house number only in
                // the formatted address ("1 North Street, …"). US/CA put the
                // number FIRST. When the component is empty but the route is set,
                // recover a leading number from the formatted address's first
                // segment so the Hausnummer field isn't left blank.
                if (!address.streetNo && details.formatted_address) {
                    const firstSegment = String(details.formatted_address).split(',')[0].trim();
                    const m = firstSegment.match(/^(\d+\s*[a-zA-Z]?)\s+(.+)$/);
                    if (m) {
                        address.streetNo = m[1].replace(/\s+/g, '');
                        if (!address.street) address.street = m[2].trim();
                    }
                }

                // 4. Reset Session Token (End of session)
                if (typeof crypto !== "undefined") {
                    sessionToken = crypto.randomUUID();
                }
            }
        } catch (e) {
            console.error("Place Details Error:", e);
        }
    }

    function handleBlur() {
        // Small delay to allow click event on dropdown items to fire before closing
        setTimeout(() => {
            isOpen = false;
        }, 200);
    }

    function handleFocus() {
        if (suggestions.length > 0) {
            isOpen = true;
            return
        }
        if (addressInput.length > 0) {
            fetchSuggestions(addressInput);
            isOpen = true;
            return
        }
    }
</script>

<div>
    <TextInput
            name="name"
            bind:value={address.name}
            label={t("Vollständiger Name *")}
            placeholder={t("Kim Muster")}
            autocomplete="name"
    />
    <ValidationError message={errors.name}/>
</div>
<!-- Debug-Buttons (Testadresse / CH-Adresse) leben jetzt in Steps/Debug.svelte
     und rufen window.__m7DebugFillAddress / __m7DebugFillSwissAddress /
     __m7DebugFillAustrianAddress. -->

<div>
    <Select
            name="country"
            bind:value={address.countryId}
            options={countries}
            label={t("Land *")}
            optionClass="text-left"
            initialized={false}
            load={fetchCountries}
            autocomplete="country-name"
            itemAttrs={(opt) => ({ 'data-country-option': opt.code })}
    />
    <ValidationError message={errors.countryId}/>
</div>
<div class="relative w-full group">
    <TextInput
            name="street-address"
            bind:value={addressInput}
            label={t("Adresse *")}
            placeholder={t("Adresse eingeben (wird automatisch vervollständigt)")}
            oninput={handleInput}
            onfocus={handleFocus}
            onblur={handleBlur}
            autocomplete="street-address"
    />

    {#if isOpen}
        <ul
                class="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto ring-1 ring-black ring-opacity-5 focus:outline-none"
        >
            {#if isLoading}
                <li
                        class="px-4 py-3 text-sm text-gray-500 flex"
                >
                    <!-- Simple Spinner -->
                    <svg
                            class="animate-spin h-5 w-5 mr-2 text-gray-400"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                    >
                        <circle
                                class="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                stroke-width="4"
                        ></circle>
                        <path
                                class="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                    </svg>
                    {t("Wird geladen...")}
                </li>
            {:else}
                {#each suggestions as item}
                    <li>
                        <button
                                type="button"
                                class="w-full text-left px-4 py-2 hover:bg-gray-100 focus:bg-gray-50 transition-colors duration-150 border-b border-gray-100 last:border-0 cursor-pointer"
                                data-checkout-action="select-address-suggestion"
                                onclick={() => handleSelect(item)}
                        >
                            <div class="font-semibold text-gray-900 text-sm">
                                {item.main_text}
                            </div>
                            <div class="text-xs text-gray-500">
                                {item.secondary_text}
                            </div>
                        </button>
                    </li>
                {/each}
            {/if}
        </ul>
    {/if}
</div>

{#if showFullAddress}
    <div class="grid grid-cols-2 gap-4 p-4 rounded-md bg-gray-50">
        <TextInput
                name="street"
                bind:value={address.street}
                label={t("Straße *")}
                placeholder={t("Musterstraße")}
                required={true}
                autocomplete="address-line1"
        />
        <TextInput
                name="streetNo"
                bind:value={address.streetNo}
                label={t("Hausnummer *")}
                placeholder={t("123")}
                autocomplete="address-line2"
        />
        <TextInput name="zip" bind:value={address.zip} label={t("PLZ *")} autocomplete="postal-code"/>
        <TextInput name="city" bind:value={address.city} label={t("Ort *")} autocomplete="address-level2"/>
    </div>
    <ValidationError message={errors.street || errors.streetNo || errors.zip || errors.city}/>
{/if}
